// src/components/AnalyticsPlus/SearchFunnelOverview.tsx
import React from "react";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  SearchBreakdown,
  SearchFunnelOverviewResponse,
} from "../../api/analyticsAdmin";
import {
  formatRate,
  segmentLabel,
  SearchFunnelSegment,
} from "../../helpers/searchAnalytics";
import { formatInteger } from "../../helpers/formatNumber";
import { sortSegments } from "../../helpers/analyticsPlus";
import SectionBody from "./SectionBody";
import { SectionState } from "./useAnalyticsStream";
import { CategoryBarTick, SEGMENT_ROW_HEIGHT } from "./activityCharts";

// Conversion rates shown as grouped bars per segment, both normalized to a
// share of searches so the two bars are directly comparable.
const RATE_SERIES: { key: keyof SearchFunnelSegment; label: string; color: string }[] = [
  {
    key: "searchToOpenRate",
    label: "Search → open",
    color: "rgb(109 76 145 / 0.9)",
  },
  {
    key: "searchToOutboundRate",
    label: "Search → outbound",
    color: "rgb(139 92 246 / 0.7)",
  },
];

const SegmentChart: React.FC<{ segments: SearchFunnelSegment[] }> = ({
  segments,
}) => (
  <div className="bg-white rounded-lg shadow p-6 mb-4">
    <div style={{ height: Math.max(160, segments.length * SEGMENT_ROW_HEIGHT) + 8 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={segments}
          margin={{ top: 24, right: 24, left: 8, bottom: 5 }}
          barGap={2}
          barCategoryGap="35%"
        >
          <XAxis
            type="number"
            domain={[0, 1]}
            tickFormatter={(value: number) => formatRate(value)}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={4}
            tickLine={false}
            interval={0}
            tick={(props) => <CategoryBarTick {...props} labelFn={segmentLabel} />}
          />
          <Tooltip formatter={(value: number) => formatRate(value)} />
          <Legend />
          {RATE_SERIES.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              radius={[0, 4, 4, 0]}
              maxBarSize={18}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const SAMPLE_THRESHOLD = 10;

const SegmentTable: React.FC<{ segments: SearchFunnelSegment[] }> = ({
  segments,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-gray-500">
        <tr>
          <th className="text-left font-medium px-4 py-3">Segment</th>
          <th className="text-right font-medium px-4 py-3">Searches</th>
          <th className="text-right font-medium px-4 py-3">Opens</th>
          <th className="text-right font-medium px-4 py-3">Outbound</th>
          <th className="text-right font-medium px-4 py-3">Searchers</th>
          <th className="text-right font-medium px-4 py-3">S→O</th>
          <th className="text-right font-medium px-4 py-3">O→Out</th>
          <th className="text-right font-medium px-4 py-3">S→Out</th>
        </tr>
      </thead>
      <tbody>
        {segments.map((s) => (
          <tr
            key={s.name}
            className={`border-t border-gray-100 ${
              s.searches < SAMPLE_THRESHOLD ? "text-gray-400" : ""
            }`}
          >
            <td className="px-4 py-3">{segmentLabel(s.name)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.searches)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.opens)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.outbounds)}</td>
            <td className="px-4 py-3 text-right">
              {formatInteger(s.uniqueSearchers)}
            </td>
            <td className="px-4 py-3 text-right">
              {formatRate(s.searchToOpenRate)}
            </td>
            <td className="px-4 py-3 text-right">
              {formatRate(s.openToOutboundRate)}
            </td>
            <td className="px-4 py-3 text-right">
              {formatRate(s.searchToOutboundRate)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export interface SearchBreakdownSection {
  key: SearchBreakdown;
  label: string;
  section: SectionState<SearchFunnelOverviewResponse>;
}

/**
 * The headline funnel and its three breakdowns, each settling on its own — the
 * same fix as on the Clicks tab, where four requests behind one Promise.all meant
 * one slow breakdown blanked the funnel too.
 */
const SearchFunnelOverview: React.FC<{
  headline: SectionState<SearchFunnelOverviewResponse>;
  breakdowns: SearchBreakdownSection[];
}> = ({ headline, breakdowns }) => {
  return (
    <section className="mb-12">
      <h3 className="text-lg font-medium mb-4">Search funnel</h3>

      <SectionBody
        section={headline}
        isEmpty={(data) => !data.segments[0] || data.segments[0].searches === 0}
        emptyMessage="No searches for this range."
      >
        {(data) => {
          // The API's `all` segment verbatim: it already carries exact rates, and
          // SearchFunnelSegment is structurally a superset of FunnelTotals.
          const totals = data.segments[0];
          return (
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            {[
              {
                label: "Searches",
                pct: 1,
                value: "100%",
                note: `${formatInteger(totals.searches)} searches`,
              },
              {
                label: "Opens",
                pct: totals.searchToOpenRate,
                value: formatRate(totals.searchToOpenRate),
                note: "of searches led to an open",
              },
              {
                label: "Outbound clicks",
                pct: totals.searchToOutboundRate,
                value: formatRate(totals.searchToOutboundRate),
                note: "of searches led to an outbound",
              },
            ].map((stage) => (
              <div key={stage.label} className="flex items-center gap-3">
                <div className="w-40">
                  <div className="text-sm text-gray-600">{stage.label}</div>
                  <div className="text-xs text-gray-400">{stage.note}</div>
                </div>
                <div className="flex-1 bg-gray-100 rounded h-8 overflow-hidden">
                  <div
                    className="bg-primary h-8 rounded flex items-center justify-center"
                    style={{ width: `${Math.round(stage.pct * 100)}%` }}
                  >
                    <span className="text-sm font-medium text-white whitespace-nowrap">
                      {stage.value}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <div className="text-xs text-gray-500 pt-2">
              {formatRate(totals.openToOutboundRate)} of opens led to an outbound ·{" "}
              {formatInteger(totals.uniqueSearchers)} unique searchers
            </div>
          </div>
          );
        }}
      </SectionBody>

      <div className="mt-10">
        <h3 className="text-lg font-medium mb-4">Breakdown</h3>
        <div className="space-y-8">
          {breakdowns.map((breakdown) => (
            <div key={breakdown.key}>
              <h4 className="text-sm font-medium text-gray-600 mb-2">
                {breakdown.label}
              </h4>
              <SectionBody
                section={breakdown.section}
                isEmpty={(data) => data.segments.length === 0}
                emptyMessage="No data for this range."
              >
                {(data) => {
                  const segments = sortSegments(
                    breakdown.key,
                    data.segments,
                    (s) => s.name,
                    (s) => s.searches
                  );
                  return (
                    <>
                      <SegmentChart segments={segments} />
                      <SegmentTable segments={segments} />
                    </>
                  );
                }}
              </SectionBody>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SearchFunnelOverview;
